/**
 * Check Library Page
 *
 * Super-admin authoring surface for the catalogue of canonical PRE checks
 * (`PRECheckDefinition`). Rules reference checks by `checkId`; this page edits
 * the user-facing wording template and contact/send bucket for each check,
 * which in turn drives every tenant Policies bullet that uses the check.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Input,
  Label,
  Modal,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { ArrowLeft, BookOpen, Loader2, Pencil, Search } from 'lucide-react';
import type { PRECheckDefinition, RuleType } from '@respectly/shared-types';
import { renderRequirement } from '@respectly/utils';
import { usePolicyStore } from '../../stores/usePolicyStore';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

export function PolicyCheckLibrary() {
  const checkDefinitions = usePolicyStore((s) => s.checkDefinitions);
  const isLoading = usePolicyStore((s) => s.isLoadingCheckDefinitions);
  const loadCheckDefinitions = usePolicyStore((s) => s.loadCheckDefinitions);
  const updateCheckDefinition = usePolicyStore((s) => s.updateCheckDefinition);
  const isUpdating = usePolicyStore((s) => s.isUpdating);

  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState<'all' | RuleType>('all');
  const [editing, setEditing] = useState<PRECheckDefinition | null>(null);

  useEffect(() => {
    loadCheckDefinitions();
  }, [loadCheckDefinitions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return checkDefinitions.filter((d) => {
      if (bucketFilter !== 'all' && d.ruleType !== bucketFilter) return false;
      if (!q) return true;
      return (
        d.checkId.toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q) ||
        d.userFacingTemplate.toLowerCase().includes(q)
      );
    });
  }, [checkDefinitions, search, bucketFilter]);

  return (
    <>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col h-full"
      >
        <motion.div
          variants={itemVariants}
          className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-center gap-4">
            <Link
              to="/settings/policy"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900/40 dark:to-indigo-800/40 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Check Library
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Catalogue of canonical PRE checks. Editing a check's template or
                bucket updates every rule bullet that uses it.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="flex-shrink-0 px-6 py-4 flex flex-wrap items-center gap-3 border-b border-slate-200 dark:border-slate-800">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by check ID, name, or template…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={bucketFilter} onValueChange={(v) => setBucketFilter(v as 'all' | RuleType)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              <SelectItem value="OPT_IN">Opt-in</SelectItem>
              <SelectItem value="SENDING">Sending</SelectItem>
              <SelectItem value="OPT_OUT">Opt-out</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="outline" className="tabular-nums ml-auto">
            {filtered.length} of {checkDefinitions.length}
          </Badge>
        </motion.div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && checkDefinitions.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-12">
              No checks match the current filter.
            </p>
          ) : (
            <div className="grid gap-3 max-w-5xl">
              {filtered.map((def) => (
                <CheckRow key={def.checkId} def={def} onEdit={() => setEditing(def)} />
              ))}
            </div>
          )}
        </div>
      </motion.div>

      <CheckEditDialog
        def={editing}
        onClose={() => setEditing(null)}
        onSave={async (dto) => {
          if (!editing?.id) return false;
          const ok = await updateCheckDefinition(editing.id, dto);
          if (ok) setEditing(null);
          return ok;
        }}
        isSaving={isUpdating}
      />
    </>
  );
}

function CheckRow({ def, onEdit }: { def: PRECheckDefinition; onEdit: () => void }) {
  // Render a sample bullet using the existing template + an empty params object
  // so authors see exactly the template text (with `{paramName}` tokens visible).
  const sample = renderRequirement(
    { description: def.description, sourceRef: null, userFacingSummary: null, userFacingCitation: null },
    { checkId: def.checkId, required: true, params: null },
    new Map([[def.checkId, def]]),
  );

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                {def.checkId}
              </code>
              <Badge variant="outline" className="text-[10px]">
                {def.ruleType === 'OPT_IN' ? 'Opt-in' : def.ruleType === 'OPT_OUT' ? 'Opt-out' : 'Sending'}
              </Badge>
              {def.id ? null : <Badge variant="secondary" className="text-[10px]">Local</Badge>}
            </div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mt-1">
              {def.name}
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={onEdit} disabled={!def.id}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" />
            Edit
          </Button>
        </div>

        <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">
            Tenant-facing template
          </p>
          <p className="text-sm text-slate-800 dark:text-slate-200">{sample.text}</p>
        </div>

        {def.paramSchema && Object.keys(def.paramSchema).length > 0 && (
          <div className="text-xs text-slate-500 dark:text-slate-400">
            <span className="font-medium">Params:</span>{' '}
            {Object.entries(def.paramSchema).map(([k, v]) => (
              <code key={k} className="font-mono mr-2">
                {k}:{String(v)}
              </code>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CheckEditDialog({
  def,
  onClose,
  onSave,
  isSaving,
}: {
  def: PRECheckDefinition | null;
  onClose: () => void;
  onSave: (dto: { userFacingTemplate: string; ruleType: RuleType; name: string; description: string }) => Promise<boolean>;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState('');
  const [bucket, setBucket] = useState<RuleType>('OPT_IN');

  useEffect(() => {
    if (def) {
      setName(def.name);
      setDescription(def.description);
      setTemplate(def.userFacingTemplate);
      setBucket(def.ruleType);
    }
  }, [def]);

  if (!def) return null;

  const preview = renderRequirement(
    { description, sourceRef: null, userFacingSummary: null, userFacingCitation: null },
    { checkId: def.checkId, required: true, params: null },
    new Map([[def.checkId, { ...def, userFacingTemplate: template, ruleType: bucket }]]),
  );

  return (
    <Modal isOpen={!!def} onClose={onClose} size="default" title={`Edit check: ${def.checkId}`}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          await onSave({
            name: name.trim(),
            description: description.trim(),
            userFacingTemplate: template.trim(),
            ruleType: bucket,
          });
        }}
        className="space-y-4 pt-2"
      >
        <div className="space-y-1.5">
          <Label className="text-sm">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm">Description (internal)</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm">Tenant-facing template</Label>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            Use <code className="font-mono">{'{paramName}'}</code> placeholders. They are
            substituted from each rule's per-check params at display time.
          </p>
          <Input value={template} onChange={(e) => setTemplate(e.target.value)} />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm">Section</Label>
          <Select value={bucket} onValueChange={(v) => setBucket(v as RuleType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OPT_IN">Opt-in</SelectItem>
              <SelectItem value="SENDING">Sending</SelectItem>
              <SelectItem value="OPT_OUT">Opt-out</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-md border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/20 p-3">
          <p className="text-[11px] uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-1">
            Preview
          </p>
          <p className="text-sm text-slate-800 dark:text-slate-200">{preview.text}</p>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving || !template.trim()}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
